import React, { useState, useEffect, useCallback } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { makeRequest2 } from '../../../../../../utils/makeRequest';
import { useFacilityStore } from '../../../../../../app/z-store/store';
import { toastify } from '../../../../../../utils/toast';

const PAGE_SIZE = 100;

const PAYMENT_TYPES = ['Prepaid', 'Postpaid', 'Token Based'];
const ASSIGN_REQUIRED_COLUMNS = ['meterAccountNumber', 'customerEmail', 'paymentType'];
const ASSIGN_OPTIONAL_COLUMNS = ['accountBalance'];
const ASSIGN_ALL_COLUMNS = [...ASSIGN_REQUIRED_COLUMNS, ...ASSIGN_OPTIONAL_COLUMNS];

const PAYMENT_REQUIRED_COLUMNS = ['accountNumber', 'reference', 'amount'];
const PAYMENT_ALL_COLUMNS = [...PAYMENT_REQUIRED_COLUMNS];

const EMPTY_COUNTS = {
    totalMeters: 0,
    totalInstalled: 0,
    liveStatus: { on: 0, off: 0 },
    meterLiveStatus: { online: 0, offline: 0 },
};

const PowerMetersTab = () => {
    const navigate = useNavigate();
    const [powerMeters, setPowerMeters] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const [first, setFirst] = useState(0);
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(null);

    const [facilityCounts, setFacilityCounts] = useState(EMPTY_COUNTS);
    const [countsLoading, setCountsLoading] = useState(true);

    // ─── Bulk assign dialog state ───────────────────────────────────────────────
    const [showAssignDialog, setShowAssignDialog] = useState(false);
    const [assignFile, setAssignFile] = useState(null);
    const [assignFileName, setAssignFileName] = useState('');
    const [assignLoading, setAssignLoading] = useState(false);
    const [assignPreview, setAssignPreview] = useState([]);
    const [assignValidCount, setAssignValidCount] = useState(0);
    const [assignErrors, setAssignErrors] = useState([]);
    const [assignStep, setAssignStep] = useState('upload'); // 'upload' | 'preview' | 'result'
    const [assignResult, setAssignResult] = useState(null);
    // ────────────────────────────────────────────────────────────────────────────

    // ─── Bulk payment upload dialog state ──────────────────────────────────────
    const [showPaymentDialog, setShowPaymentDialog] = useState(false);
    const [paymentFile, setPaymentFile] = useState(null);
    const [paymentFileName, setPaymentFileName] = useState('');
    const [paymentLoading, setPaymentLoading] = useState(false);
    const [paymentPreview, setPaymentPreview] = useState([]);
    const [paymentValidCount, setPaymentValidCount] = useState(0);
    const [paymentErrors, setPaymentErrors] = useState([]);
    const [paymentStep, setPaymentStep] = useState('upload'); // 'upload' | 'preview' | 'result'
    const [paymentResult, setPaymentResult] = useState(null);
    // ────────────────────────────────────────────────────────────────────────────

    const selectedFacilityId = useFacilityStore((state) => state.facilityId);

    const fetchFacilityCounts = useCallback(async () => {
        if (!selectedFacilityId) return;
        try {
            setCountsLoading(true);
            const response = await makeRequest2(
                `/api/app/power_meter_management/controllers/power_meters/facility_meters_count/${selectedFacilityId}`,
                'GET'
            );
            if (response?.data?.data) {
                setFacilityCounts(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching facility counts:', err);
        } finally {
            setCountsLoading(false);
        }
    }, [selectedFacilityId]);

    const fetchPowerMeters = useCallback(async (page = 1, field = sortField, order = sortOrder) => {
        if (!selectedFacilityId) {
            toastify('No facility ID available', 'error');
            return;
        }
        try {
            setLoading(true);
            const params = {
                page,
                limit: PAGE_SIZE,
                ...(searchTerm && { search: searchTerm }),
                ...(field && { sortField: field }),
                ...(order && { sortOrder: order === 1 ? 'asc' : 'desc' }),
            };
            const response = await makeRequest2(
                `/api/app/power_meter_management/controllers/power_meters/get_all/${selectedFacilityId}?${new URLSearchParams(params)}`,
                'GET'
            );
            
            console.log('API Response:', response); // Debug log
            
            if (response?.data) {
                // Handle different response structures
                let dataArray = [];
                let total = 0;
                
                if (Array.isArray(response.data)) {
                    dataArray = response.data;
                    total = dataArray.length;
                } else if (response.data.data) {
                    if (Array.isArray(response.data.data)) {
                        dataArray = response.data.data;
                        total = dataArray.length;
                    } else if (response.data.data.data) {
                        dataArray = response.data.data.data;
                        total = response.data.data.pagination?.totalCount || dataArray.length;
                    }
                }
                
                setPowerMeters(dataArray);
                setTotalCount(total);
            } else {
                toastify(response?.error || 'Failed to fetch power meters', 'error');
                setPowerMeters([]);
                setTotalCount(0);
            }
        } catch (error) {
            console.error('Error fetching power meters:', error);
            toastify('An error occurred while fetching power meters', 'error');
            setPowerMeters([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    }, [selectedFacilityId, searchTerm, sortField, sortOrder]);

    useEffect(() => {
        if (selectedFacilityId) {
            fetchFacilityCounts();
            fetchPowerMeters(1);
        }
    }, [selectedFacilityId]);

    useEffect(() => {
        if (!selectedFacilityId) return;
        const delay = setTimeout(() => {
            setFirst(0);
            fetchPowerMeters(1, sortField, sortOrder);
        }, 400);
        return () => clearTimeout(delay);
    }, [searchTerm]);

    const onPage = (event) => {
        const newPage = Math.floor(event.first / PAGE_SIZE) + 1;
        setFirst(event.first);
        fetchPowerMeters(newPage, sortField, sortOrder);
    };

    const onSort = (event) => {
        const { sortField: field, sortOrder: order } = event;
        setSortField(field);
        setSortOrder(order);
        setFirst(0);
        fetchPowerMeters(1, field, order);
    };

    // Column body templates
    const meterStatusTemplate = (rowData) => {
        const map = { on: 'success', off: 'danger', maintenance: 'warning', faulty: 'danger' };
        const s = (rowData.liveStatus || '').toLowerCase();
        return <span className={`badge rounded-pill text-bg-${map[s] || 'secondary'}`}>{rowData.liveStatus || 'Off'}</span>;
    };

    const meterLiveStatusTemplate = (rowData) => {
        const map = { online: 'success', offline: 'danger', maintenance: 'warning', faulty: 'danger' };
        const s = (rowData.meterLiveStatus || '').toLowerCase();
        return <span className={`badge rounded-pill text-bg-${map[s] || 'secondary'}`}>{rowData.meterLiveStatus || 'Offline'}</span>;
    };

    const unitTemplate = (rowData) =>
        rowData.unitInfo?.unitName || rowData.unitName || '-';

    const actionsTemplate = (rowData) => (
        <Button
            icon="ti ti-eye"
            className="p-button-rounded p-button-text"
            onClick={() => navigate(`/facility/utility_management/meter_management/view_power_meter/${rowData._id}`)}
            tooltip="View Meter"
        />
    );

    // ─── Bulk assign helpers ────────────────────────────────────────────────────

    /** Parse the first sheet of an .xlsx/.xls workbook, or a .csv file, into {headers, rows} */
    const parseTabularFile = async (file) => {
        const ext = file.name.split('.').pop().toLowerCase();

        if (ext === 'csv') {
            const text = await file.text();
            const lines = text.trim().split(/\r?\n/);
            if (lines.length < 2) return { headers: [], rows: [] };
            const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
            const rows = lines.slice(1).map((line, idx) => {
                const values = [];
                let current = '';
                let inQuotes = false;
                for (const ch of line) {
                    if (ch === '"') { inQuotes = !inQuotes; continue; }
                    if (ch === ',' && !inQuotes) { values.push(current.trim()); current = ''; continue; }
                    current += ch;
                }
                values.push(current.trim());
                const obj = { _rowIndex: idx + 2 };
                headers.forEach((h, i) => { obj[h] = values[i] ?? ''; });
                return obj;
            }).filter(r => Object.values(r).some(v => v !== '' && v !== undefined));
            return { headers, rows };
        }

        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const aoa = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '' });
        if (aoa.length < 2) return { headers: [], rows: [] };
        const headers = aoa[0].map(h => String(h ?? '').trim());
        const rows = aoa.slice(1).map((line, idx) => {
            const obj = { _rowIndex: idx + 2 };
            headers.forEach((h, i) => { obj[h] = String(line[i] ?? '').trim(); });
            return obj;
        }).filter(r => Object.values(r).some(v => v !== '' && v !== undefined));
        return { headers, rows };
    };

    /** Client-side row validation, mirroring the server's checks */
    const validateAssignRows = (headers, rows) => {
        const missingRequired = ASSIGN_REQUIRED_COLUMNS.filter(c => !headers.includes(c));
        if (missingRequired.length > 0) {
            return { validRows: [], errors: [`Missing required columns: ${missingRequired.join(', ')}`], headerError: true };
        }

        const errors = [];
        const validRows = [];

        rows.forEach(row => {
            const rowErrors = [];
            if (!row.meterAccountNumber?.trim() || isNaN(Number(row.meterAccountNumber))) {
                rowErrors.push('meterAccountNumber is required and must be a number');
            }
            if (!row.customerEmail?.trim()) {
                rowErrors.push('customerEmail is empty');
            }
            if (!PAYMENT_TYPES.includes(row.paymentType?.trim())) {
                rowErrors.push(`paymentType must be one of ${PAYMENT_TYPES.join(', ')}`);
            }
            if (row.accountBalance && isNaN(Number(row.accountBalance))) {
                rowErrors.push('accountBalance must be a number');
            }

            if (rowErrors.length > 0) {
                errors.push({ row: row._rowIndex, errors: rowErrors });
            } else {
                validRows.push(row);
            }
        });

        return { validRows, errors, headerError: false };
    };

    const handleAssignFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setAssignFile(file);
        setAssignFileName(file.name);
        setAssignErrors([]);
        setAssignPreview([]);

        const ext = file.name.split('.').pop().toLowerCase();
        if (!['csv', 'xlsx', 'xls'].includes(ext)) {
            setAssignErrors(['Only CSV or Excel (.xlsx/.xls) files are supported.']);
            return;
        }

        const { headers, rows } = await parseTabularFile(file);
        if (rows.length === 0) {
            setAssignErrors(['The file appears to be empty or has no data rows.']);
            return;
        }

        const { validRows, errors, headerError } = validateAssignRows(headers, rows);
        setAssignPreview(rows.slice(0, 10));
        setAssignValidCount(validRows.length);
        setAssignErrors(errors);

        if (!headerError && validRows.length > 0) {
            setAssignStep('preview');
        }
    };

    const downloadAssignTemplate = () => {
        const exampleRow = {
            meterAccountNumber: '260134',
            customerEmail: 'customer@example.com',
            paymentType: 'Prepaid',
            accountBalance: '0',
        };
        const worksheet = XLSX.utils.json_to_sheet([exampleRow], { header: ASSIGN_ALL_COLUMNS });
        worksheet['!cols'] = ASSIGN_ALL_COLUMNS.map(c => ({ wch: Math.max(c.length + 2, 18) }));
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Bulk Assign');
        XLSX.writeFile(workbook, 'bulk_assign_customers_template.xlsx');
    };

    const handleAssignSubmit = async () => {
        if (!assignFile || !selectedFacilityId) return;

        const { headers, rows } = await parseTabularFile(assignFile);
        const { validRows, errors } = validateAssignRows(headers, rows);

        if (errors.length > 0 && validRows.length === 0) {
            toastify('Please fix all errors before importing', 'error');
            return;
        }

        setAssignLoading(true);
        try {
            const response = await makeRequest2(
                `/api/app/power_meter_management/controllers/customer_management/bulk_add_customer_accounts`,
                'POST',
                { facilityId: selectedFacilityId, assignments: validRows }
            );

            if (response.success && response.data?.success) {
                const result = response.data?.data ?? {};
                const hardErrors = result.errors ?? [];
                setAssignResult({
                    success: (result.assigned ?? 0) > 0 || hardErrors.length === 0,
                    total: result.total ?? validRows.length,
                    assigned: result.assigned ?? 0,
                    skipped: result.skipped ?? 0,
                    errors: hardErrors,
                });
                setAssignStep('result');
            } else {
                toastify(response.data?.error || response.error || 'Bulk assignment failed', 'error');
            }
        } catch (err) {
            console.error('Bulk assign error:', err);
            toastify('An unexpected error occurred during bulk assignment', 'error');
        } finally {
            setAssignLoading(false);
        }
    };

    const resetAssignDialog = () => {
        setAssignFile(null);
        setAssignFileName('');
        setAssignErrors([]);
        setAssignPreview([]);
        setAssignValidCount(0);
        setAssignStep('upload');
        setAssignResult(null);
    };

    const closeAssignDialog = () => {
        setShowAssignDialog(false);
        resetAssignDialog();
    };
    // ────────────────────────────────────────────────────────────────────────────

    // ─── Bulk payment upload helpers ────────────────────────────────────────────

    const validatePaymentRows = (headers, rows) => {
        const missingRequired = PAYMENT_REQUIRED_COLUMNS.filter(c => !headers.includes(c));
        if (missingRequired.length > 0) {
            return { validRows: [], errors: [`Missing required columns: ${missingRequired.join(', ')}`], headerError: true };
        }

        const errors = [];
        const validRows = [];

        rows.forEach(row => {
            const rowErrors = [];
            if (!row.accountNumber?.trim()) {
                rowErrors.push('accountNumber is empty');
            }
            if (!row.reference?.trim()) {
                rowErrors.push('reference is empty');
            }
            if (!row.amount?.trim() || isNaN(Number(row.amount)) || Number(row.amount) <= 0) {
                rowErrors.push('amount must be a number greater than 0');
            }

            if (rowErrors.length > 0) {
                errors.push({ row: row._rowIndex, errors: rowErrors });
            } else {
                validRows.push(row);
            }
        });

        return { validRows, errors, headerError: false };
    };

    const handlePaymentFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setPaymentFile(file);
        setPaymentFileName(file.name);
        setPaymentErrors([]);
        setPaymentPreview([]);

        const ext = file.name.split('.').pop().toLowerCase();
        if (!['csv', 'xlsx', 'xls'].includes(ext)) {
            setPaymentErrors(['Only CSV or Excel (.xlsx/.xls) files are supported.']);
            return;
        }

        const { headers, rows } = await parseTabularFile(file);
        if (rows.length === 0) {
            setPaymentErrors(['The file appears to be empty or has no data rows.']);
            return;
        }

        const { validRows, errors, headerError } = validatePaymentRows(headers, rows);
        setPaymentPreview(rows.slice(0, 10));
        setPaymentValidCount(validRows.length);
        setPaymentErrors(errors);

        if (!headerError && validRows.length > 0) {
            setPaymentStep('preview');
        }
    };

    const downloadPaymentTemplate = () => {
        const exampleRow = {
            accountNumber: '260134',
            reference: 'MPESA-CODE-OR-BATCH-REF-001',
            amount: '500',
        };
        const worksheet = XLSX.utils.json_to_sheet([exampleRow], { header: PAYMENT_ALL_COLUMNS });
        worksheet['!cols'] = PAYMENT_ALL_COLUMNS.map(c => ({ wch: Math.max(c.length + 2, 22) }));
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Bulk Payments');
        XLSX.writeFile(workbook, 'bulk_payments_template.xlsx');
    };

    const handlePaymentSubmit = async () => {
        if (!paymentFile) return;

        const { headers, rows } = await parseTabularFile(paymentFile);
        const { validRows, errors } = validatePaymentRows(headers, rows);

        if (errors.length > 0 && validRows.length === 0) {
            toastify('Please fix all errors before importing', 'error');
            return;
        }

        setPaymentLoading(true);
        try {
            const response = await makeRequest2(
                `/api/app/power_meter_management/controllers/customer_management/bulk_add_credit_to_customers`,
                'POST',
                { payments: validRows }
            );

            if (response.success && response.data?.success) {
                const result = response.data?.data ?? {};
                const hardErrors = result.errors ?? [];
                setPaymentResult({
                    success: (result.queued ?? 0) > 0 || hardErrors.length === 0,
                    total: result.total ?? validRows.length,
                    queued: result.queued ?? 0,
                    skipped: result.skipped ?? 0,
                    errors: hardErrors,
                });
                setPaymentStep('result');
            } else {
                toastify(response.data?.error || response.error || 'Bulk payment upload failed', 'error');
            }
        } catch (err) {
            console.error('Bulk payment upload error:', err);
            toastify('An unexpected error occurred during bulk payment upload', 'error');
        } finally {
            setPaymentLoading(false);
        }
    };

    const resetPaymentDialog = () => {
        setPaymentFile(null);
        setPaymentFileName('');
        setPaymentErrors([]);
        setPaymentPreview([]);
        setPaymentValidCount(0);
        setPaymentStep('upload');
        setPaymentResult(null);
    };

    const closePaymentDialog = () => {
        setShowPaymentDialog(false);
        resetPaymentDialog();
    };
    // ────────────────────────────────────────────────────────────────────────────

    const SummaryBar = () => {
        if (countsLoading) {
            return (
                <div className="d-flex align-items-center gap-2 mb-3">
                    <div className="spinner-border spinner-border-sm text-primary" role="status" />
                    <span className="text-muted small">Loading facility counts...</span>
                </div>
            );
        }

        const { totalMeters, totalInstalled, liveStatus, meterLiveStatus } = facilityCounts;

        return (
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3 p-3 rounded"
                style={{ background: '#f8f9fa', border: '1px solid #dee2e6' }}>

                <div className="d-flex flex-column align-items-center px-3 py-1 rounded bg-white border">
                    <span className="fw-bold fs-5">{totalMeters}</span>
                    <span className="text-muted" style={{ fontSize: '0.75rem' }}>Total Meters</span>
                </div>

                <div className="d-flex flex-column align-items-center px-3 py-1 rounded bg-white border">
                    <span className="fw-bold fs-5">{totalInstalled}</span>
                    <span className="text-muted" style={{ fontSize: '0.75rem' }}>Installed</span>
                </div>

                <div className="vr mx-1" style={{ height: 40 }} />

                <span className="text-muted small fw-semibold">POWER STATUS:</span>

                <div className="d-flex align-items-center gap-1 px-3 py-1 rounded"
                    style={{ background: '#d1e7dd', border: '1px solid #a3cfbb' }}>
                    <span className="badge bg-success" style={{ fontSize: '0.7rem' }}>ON</span>
                    <span className="fw-bold text-success fs-6">{liveStatus.on}</span>
                </div>

                <div className="d-flex align-items-center gap-1 px-3 py-1 rounded"
                    style={{ background: '#f8d7da', border: '1px solid #f1aeb5' }}>
                    <span className="badge bg-danger" style={{ fontSize: '0.7rem' }}>OFF</span>
                    <span className="fw-bold text-danger fs-6">{liveStatus.off}</span>
                </div>

                <div className="vr mx-1" style={{ height: 40 }} />

                <span className="text-muted small fw-semibold">CONNECTIVITY:</span>

                <div className="d-flex align-items-center gap-1 px-3 py-1 rounded"
                    style={{ background: '#d1e7dd', border: '1px solid #a3cfbb' }}>
                    <i className="ti ti-wifi text-success" />
                    <span className="fw-bold text-success fs-6">{meterLiveStatus.online}</span>
                    <span className="text-success small">Online</span>
                </div>

                <div className="d-flex align-items-center gap-1 px-3 py-1 rounded"
                    style={{ background: '#f8d7da', border: '1px solid #f1aeb5' }}>
                    <i className="ti ti-wifi-off text-danger" />
                    <span className="fw-bold text-danger fs-6">{meterLiveStatus.offline}</span>
                    <span className="text-danger small">Offline</span>
                </div>

                <button
                    className="btn btn-sm btn-outline-secondary ms-auto"
                    onClick={fetchFacilityCounts}
                    title="Refresh counts"
                >
                    <i className="ti ti-refresh" />
                </button>
            </div>
        );
    };

    return (
        <div className="card">
            <div className="card-body">
                <SummaryBar />

                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                    <div className="col-md-3 col-xs-12">
                        <input
                            className="form-control"
                            placeholder="Search by Device ID, Unit Name, Serial No..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="d-flex gap-2">
                        <Button
                            label="Bulk Assign Customers"
                            icon="ti ti-users-plus"
                            className="btn btn-primary btn-sm"
                            onClick={() => setShowAssignDialog(true)}
                        />
                        <Button
                            label="Bulk Upload Payments"
                            icon="ti ti-cash"
                            className="btn btn-success btn-sm"
                            onClick={() => setShowPaymentDialog(true)}
                        />
                    </div>
                </div>

                <div className="dt-responsive table-responsive">
                    <DataTable
                        value={powerMeters}
                        loading={loading}
                        emptyMessage="No power meters found."
                        sortMode="single"
                        sortField={sortField}
                        sortOrder={sortOrder}
                        onSort={onSort}
                        paginator
                        lazy
                        rows={PAGE_SIZE}
                        first={first}
                        totalRecords={totalCount}
                        onPage={onPage}
                        stripedRows
                        tableStyle={{ minWidth: '50rem' }}
                        dataKey="_id"
                    >
                        <Column field="deviceId" header="Device ID" sortable />
                        <Column header="Unit" body={unitTemplate} />
                        <Column field="type" header="Meter Type" sortable />
                        <Column
                            field="previousMeterReading" 
                            header="Initial V (kWh)"
                            body={(r) => r.previousMeterReading != null ? `${r.previousMeterReading} kWh` : '-'}
                            sortable
                        />
                        <Column
                            field="currentMeterReading"  
                            header="Current V (kWh)"
                            body={(r) => r.currentMeterReading != null ? `${r.currentMeterReading} kWh` : '-'}
                            sortable
                        />
                        <Column field="liveStatus" header="Status" body={meterStatusTemplate} sortable />
                        <Column field="meterLiveStatus" header="Live Status" body={meterLiveStatusTemplate} sortable />
                        <Column
                            field="lastUpdated"            
                            header="Last Updated"
                            body={(r) => r.lastUpdated ? new Date(r.lastUpdated).toLocaleDateString() : '-'}
                            sortable
                        />
                        <Column header="Actions" body={actionsTemplate} />
                    </DataTable>
                </div>
            </div>

            {/* ── Bulk Assign Customers Dialog ── */}
            <Dialog
                header="Bulk Assign Customers to Meters"
                visible={showAssignDialog}
                style={{ width: '55vw', minWidth: 340 }}
                onHide={closeAssignDialog}
                modal
            >
                {assignStep === 'upload' && (
                    <div>
                        <div className="alert alert-info d-flex align-items-start gap-2 mb-4" style={{ borderRadius: 8 }}>
                            <i className="ti ti-info-circle mt-1" />
                            <div>
                                <strong>Before you start:</strong> the customer must already exist (by email), and the
                                meter must already exist (by its account number) in this facility. No SMS or email is
                                sent for bulk assignments.
                                <br />
                                <button className="btn btn-sm btn-outline-primary mt-2" onClick={downloadAssignTemplate}>
                                    <i className="ti ti-download me-1" />
                                    Download Template
                                </button>
                            </div>
                        </div>

                        <div className="mb-3">
                            <p className="text-muted mb-1" style={{ fontSize: 13 }}>
                                <strong>Required columns:</strong> {ASSIGN_REQUIRED_COLUMNS.join(', ')}
                            </p>
                            <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                                <strong>Optional columns:</strong> {ASSIGN_OPTIONAL_COLUMNS.join(', ')} (paymentType must be one of {PAYMENT_TYPES.join(', ')})
                            </p>
                        </div>

                        <label
                            htmlFor="assign-upload"
                            style={{
                                display: 'block', border: '2px dashed #d1d5db', borderRadius: 12,
                                padding: '32px 24px', textAlign: 'center', cursor: 'pointer',
                                background: assignFileName ? '#f0fdf4' : '#fafafa',
                                borderColor: assignFileName ? '#86efac' : '#d1d5db',
                            }}
                        >
                            <input id="assign-upload" type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }} onChange={handleAssignFileChange} />
                            {assignFileName ? (
                                <>
                                    <i className="ti ti-file-check" style={{ fontSize: 36, color: '#22c55e' }} />
                                    <p className="mt-2 mb-0 fw-semibold">{assignFileName}</p>
                                    <p className="text-muted mt-1" style={{ fontSize: 13 }}>Click to choose a different file</p>
                                </>
                            ) : (
                                <>
                                    <i className="ti ti-cloud-upload" style={{ fontSize: 36, color: '#9ca3af' }} />
                                    <p className="mt-2 mb-1 fw-semibold">Click to upload CSV or Excel file</p>
                                    <p className="text-muted mb-0" style={{ fontSize: 13 }}>.csv, .xlsx, and .xls files are accepted</p>
                                </>
                            )}
                        </label>

                        {assignErrors.length > 0 && (
                            <div className="alert alert-danger mt-3" style={{ fontSize: 13, borderRadius: 8, maxHeight: 150, overflowY: 'auto' }}>
                                {assignErrors.map((e, i) => (
                                    <div key={i}>{typeof e === 'string' ? e : `Row ${e.row}: ${e.errors.join('; ')}`}</div>
                                ))}
                            </div>
                        )}

                        {assignValidCount > 0 && (
                            <div className={`alert ${assignErrors.length === 0 ? 'alert-success' : 'alert-warning'} mt-3`} style={{ borderRadius: 8 }}>
                                <strong>{assignValidCount}</strong> row{assignValidCount === 1 ? '' : 's'} parsed successfully
                                {assignErrors.length > 0 && <> (<strong>{assignErrors.length}</strong> will be skipped due to errors)</>}.
                                Click <strong>Preview &amp; Validate</strong> to continue.
                            </div>
                        )}

                        <div className="d-flex justify-content-end gap-2 mt-4">
                            <button className="btn btn-outline-secondary" onClick={closeAssignDialog}>Cancel</button>
                            <button
                                className="btn btn-primary"
                                disabled={!assignFileName || assignValidCount === 0}
                                onClick={() => setAssignStep('preview')}
                            >
                                Preview &amp; Validate <i className="ti ti-arrow-right ms-1" />
                            </button>
                        </div>
                    </div>
                )}

                {assignStep === 'preview' && (
                    <div>
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <h6 className="mb-0">Preview (first 10 rows)</h6>
                            {assignErrors.length > 0 ? (
                                <span className="badge bg-danger">{assignErrors.length} row(s) with errors</span>
                            ) : (
                                <span className="badge bg-success">All rows valid</span>
                            )}
                        </div>

                        <div style={{ overflowX: 'auto', maxHeight: 280, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                            <table className="table table-sm table-striped mb-0" style={{ fontSize: 12 }}>
                                <thead style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 1 }}>
                                    <tr>
                                        <th>#</th>
                                        {ASSIGN_REQUIRED_COLUMNS.map(c => <th key={c}>{c} <span style={{ color: 'red' }}>*</span></th>)}
                                        {ASSIGN_OPTIONAL_COLUMNS.map(c => <th key={c}>{c}</th>)}
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {assignPreview.map((row, i) => {
                                        const rowErr = assignErrors.find(e => e.row === row._rowIndex);
                                        return (
                                            <tr key={i} style={{ background: rowErr ? '#fef2f2' : undefined }}>
                                                <td>{row._rowIndex}</td>
                                                {ASSIGN_REQUIRED_COLUMNS.map(c => <td key={c}>{row[c] || <span style={{ color: '#9ca3af' }}>—</span>}</td>)}
                                                {ASSIGN_OPTIONAL_COLUMNS.map(c => <td key={c}>{row[c] || <span style={{ color: '#9ca3af' }}>—</span>}</td>)}
                                                <td>
                                                    {rowErr
                                                        ? <span title={rowErr.errors.join('; ')} className="badge bg-danger" style={{ cursor: 'help' }}>Error</span>
                                                        : <span className="badge bg-success">OK</span>
                                                    }
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {assignErrors.length > 0 && (
                            <div className="alert alert-warning mt-3" style={{ fontSize: 13, borderRadius: 8 }}>
                                <i className="ti ti-alert-triangle me-1" />
                                Rows with errors will be <strong>skipped</strong>. Only valid rows will be assigned.
                            </div>
                        )}

                        <div className="d-flex justify-content-between mt-4">
                            <button className="btn btn-outline-secondary" onClick={() => setAssignStep('upload')}>
                                <i className="ti ti-arrow-left me-1" /> Back
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={handleAssignSubmit}
                                disabled={assignLoading || assignValidCount === 0}
                            >
                                {assignLoading
                                    ? <><span className="spinner-border spinner-border-sm me-2" />Assigning...</>
                                    : <><i className="ti ti-users-plus me-1" />Assign {assignValidCount} Customers</>
                                }
                            </button>
                        </div>
                    </div>
                )}

                {assignStep === 'result' && assignResult && (
                    <div className="text-center py-3">
                        <div style={{ fontSize: 56, marginBottom: 8 }}>
                            {assignResult.success ? '✅' : '❌'}
                        </div>
                        <h5>{assignResult.success ? 'Bulk Assignment Complete!' : 'Bulk Assignment Failed'}</h5>

                        <div className="row g-3 mt-2 text-start">
                            <div className="col-4">
                                <div className="card text-center border-0" style={{ background: '#f0fdf4', borderRadius: 10 }}>
                                    <div className="card-body py-3">
                                        <div style={{ fontSize: 28, fontWeight: 700, color: '#22c55e' }}>{assignResult.assigned}</div>
                                        <div style={{ fontSize: 12, color: '#6b7280' }}>Assigned</div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-4">
                                <div className="card text-center border-0" style={{ background: '#fffbeb', borderRadius: 10 }}>
                                    <div className="card-body py-3">
                                        <div style={{ fontSize: 28, fontWeight: 700, color: '#f59e0b' }}>{assignResult.skipped}</div>
                                        <div style={{ fontSize: 12, color: '#6b7280' }}>Skipped</div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-4">
                                <div className="card text-center border-0" style={{ background: '#f9fafb', borderRadius: 10 }}>
                                    <div className="card-body py-3">
                                        <div style={{ fontSize: 28, fontWeight: 700, color: '#6b7280' }}>{assignResult.total}</div>
                                        <div style={{ fontSize: 12, color: '#6b7280' }}>Total</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {assignResult.errors?.length > 0 && (
                            <div className="alert alert-warning text-start mt-3" style={{ fontSize: 13, borderRadius: 8, maxHeight: 150, overflowY: 'auto' }}>
                                <strong>Skipped rows:</strong>
                                <ul className="mb-0 ps-3 mt-1">
                                    {assignResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                                </ul>
                            </div>
                        )}

                        <div className="d-flex justify-content-center gap-2 mt-4">
                            <button className="btn btn-outline-secondary" onClick={resetAssignDialog}>
                                <i className="ti ti-upload me-1" /> Assign More
                            </button>
                            <button className="btn btn-primary" onClick={closeAssignDialog}>Done</button>
                        </div>
                    </div>
                )}
            </Dialog>

            {/* ── Bulk Upload Payments Dialog ── */}
            <Dialog
                header="Bulk Upload Payments"
                visible={showPaymentDialog}
                style={{ width: '55vw', minWidth: 340 }}
                onHide={closePaymentDialog}
                modal
            >
                {paymentStep === 'upload' && (
                    <div>
                        <div className="alert alert-info d-flex align-items-start gap-2 mb-4" style={{ borderRadius: 8 }}>
                            <i className="ti ti-info-circle mt-1" />
                            <div>
                                <strong>Before you start:</strong> each row is applied through the same pipeline a real
                                M-Pesa payment uses — it updates the customer's real balance/invoice and is recorded
                                in the transaction ledger (marked as a manual/bulk entry, not a fake M-Pesa receipt).
                                The account must already have an active customer assigned.
                                <br />
                                <button className="btn btn-sm btn-outline-primary mt-2" onClick={downloadPaymentTemplate}>
                                    <i className="ti ti-download me-1" />
                                    Download Template
                                </button>
                            </div>
                        </div>

                        <div className="mb-3">
                            <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                                <strong>Required columns:</strong> {PAYMENT_REQUIRED_COLUMNS.join(', ')}
                            </p>
                        </div>

                        <label
                            htmlFor="payment-upload"
                            style={{
                                display: 'block', border: '2px dashed #d1d5db', borderRadius: 12,
                                padding: '32px 24px', textAlign: 'center', cursor: 'pointer',
                                background: paymentFileName ? '#f0fdf4' : '#fafafa',
                                borderColor: paymentFileName ? '#86efac' : '#d1d5db',
                            }}
                        >
                            <input id="payment-upload" type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }} onChange={handlePaymentFileChange} />
                            {paymentFileName ? (
                                <>
                                    <i className="ti ti-file-check" style={{ fontSize: 36, color: '#22c55e' }} />
                                    <p className="mt-2 mb-0 fw-semibold">{paymentFileName}</p>
                                    <p className="text-muted mt-1" style={{ fontSize: 13 }}>Click to choose a different file</p>
                                </>
                            ) : (
                                <>
                                    <i className="ti ti-cloud-upload" style={{ fontSize: 36, color: '#9ca3af' }} />
                                    <p className="mt-2 mb-1 fw-semibold">Click to upload CSV or Excel file</p>
                                    <p className="text-muted mb-0" style={{ fontSize: 13 }}>.csv, .xlsx, and .xls files are accepted</p>
                                </>
                            )}
                        </label>

                        {paymentErrors.length > 0 && (
                            <div className="alert alert-danger mt-3" style={{ fontSize: 13, borderRadius: 8, maxHeight: 150, overflowY: 'auto' }}>
                                {paymentErrors.map((e, i) => (
                                    <div key={i}>{typeof e === 'string' ? e : `Row ${e.row}: ${e.errors.join('; ')}`}</div>
                                ))}
                            </div>
                        )}

                        {paymentValidCount > 0 && (
                            <div className={`alert ${paymentErrors.length === 0 ? 'alert-success' : 'alert-warning'} mt-3`} style={{ borderRadius: 8 }}>
                                <strong>{paymentValidCount}</strong> row{paymentValidCount === 1 ? '' : 's'} parsed successfully
                                {paymentErrors.length > 0 && <> (<strong>{paymentErrors.length}</strong> will be skipped due to errors)</>}.
                                Click <strong>Preview &amp; Validate</strong> to continue.
                            </div>
                        )}

                        <div className="d-flex justify-content-end gap-2 mt-4">
                            <button className="btn btn-outline-secondary" onClick={closePaymentDialog}>Cancel</button>
                            <button
                                className="btn btn-primary"
                                disabled={!paymentFileName || paymentValidCount === 0}
                                onClick={() => setPaymentStep('preview')}
                            >
                                Preview &amp; Validate <i className="ti ti-arrow-right ms-1" />
                            </button>
                        </div>
                    </div>
                )}

                {paymentStep === 'preview' && (
                    <div>
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <h6 className="mb-0">Preview (first 10 rows)</h6>
                            {paymentErrors.length > 0 ? (
                                <span className="badge bg-danger">{paymentErrors.length} row(s) with errors</span>
                            ) : (
                                <span className="badge bg-success">All rows valid</span>
                            )}
                        </div>

                        <div style={{ overflowX: 'auto', maxHeight: 280, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                            <table className="table table-sm table-striped mb-0" style={{ fontSize: 12 }}>
                                <thead style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 1 }}>
                                    <tr>
                                        <th>#</th>
                                        {PAYMENT_REQUIRED_COLUMNS.map(c => <th key={c}>{c} <span style={{ color: 'red' }}>*</span></th>)}
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paymentPreview.map((row, i) => {
                                        const rowErr = paymentErrors.find(e => e.row === row._rowIndex);
                                        return (
                                            <tr key={i} style={{ background: rowErr ? '#fef2f2' : undefined }}>
                                                <td>{row._rowIndex}</td>
                                                {PAYMENT_REQUIRED_COLUMNS.map(c => <td key={c}>{row[c] || <span style={{ color: '#9ca3af' }}>—</span>}</td>)}
                                                <td>
                                                    {rowErr
                                                        ? <span title={rowErr.errors.join('; ')} className="badge bg-danger" style={{ cursor: 'help' }}>Error</span>
                                                        : <span className="badge bg-success">OK</span>
                                                    }
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {paymentErrors.length > 0 && (
                            <div className="alert alert-warning mt-3" style={{ fontSize: 13, borderRadius: 8 }}>
                                <i className="ti ti-alert-triangle me-1" />
                                Rows with errors will be <strong>skipped</strong>. Only valid rows will be uploaded.
                            </div>
                        )}

                        <div className="d-flex justify-content-between mt-4">
                            <button className="btn btn-outline-secondary" onClick={() => setPaymentStep('upload')}>
                                <i className="ti ti-arrow-left me-1" /> Back
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={handlePaymentSubmit}
                                disabled={paymentLoading || paymentValidCount === 0}
                            >
                                {paymentLoading
                                    ? <><span className="spinner-border spinner-border-sm me-2" />Uploading...</>
                                    : <><i className="ti ti-cash me-1" />Upload {paymentValidCount} Payments</>
                                }
                            </button>
                        </div>
                    </div>
                )}

                {paymentStep === 'result' && paymentResult && (
                    <div className="text-center py-3">
                        <div style={{ fontSize: 56, marginBottom: 8 }}>
                            {paymentResult.success ? '✅' : '❌'}
                        </div>
                        <h5>{paymentResult.success ? 'Bulk Payment Upload Complete!' : 'Bulk Payment Upload Failed'}</h5>

                        <div className="row g-3 mt-2 text-start">
                            <div className="col-4">
                                <div className="card text-center border-0" style={{ background: '#f0fdf4', borderRadius: 10 }}>
                                    <div className="card-body py-3">
                                        <div style={{ fontSize: 28, fontWeight: 700, color: '#22c55e' }}>{paymentResult.queued}</div>
                                        <div style={{ fontSize: 12, color: '#6b7280' }}>Queued</div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-4">
                                <div className="card text-center border-0" style={{ background: '#fffbeb', borderRadius: 10 }}>
                                    <div className="card-body py-3">
                                        <div style={{ fontSize: 28, fontWeight: 700, color: '#f59e0b' }}>{paymentResult.skipped}</div>
                                        <div style={{ fontSize: 12, color: '#6b7280' }}>Skipped</div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-4">
                                <div className="card text-center border-0" style={{ background: '#f9fafb', borderRadius: 10 }}>
                                    <div className="card-body py-3">
                                        <div style={{ fontSize: 28, fontWeight: 700, color: '#6b7280' }}>{paymentResult.total}</div>
                                        <div style={{ fontSize: 12, color: '#6b7280' }}>Total</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="alert alert-info text-start mt-3" style={{ fontSize: 13, borderRadius: 8 }}>
                            <i className="ti ti-info-circle me-1" />
                            Queued rows are processed asynchronously — balances and invoices update within a few
                            seconds, same as a real payment.
                        </div>

                        {paymentResult.errors?.length > 0 && (
                            <div className="alert alert-warning text-start mt-3" style={{ fontSize: 13, borderRadius: 8, maxHeight: 150, overflowY: 'auto' }}>
                                <strong>Skipped rows:</strong>
                                <ul className="mb-0 ps-3 mt-1">
                                    {paymentResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                                </ul>
                            </div>
                        )}

                        <div className="d-flex justify-content-center gap-2 mt-4">
                            <button className="btn btn-outline-secondary" onClick={resetPaymentDialog}>
                                <i className="ti ti-upload me-1" /> Upload More
                            </button>
                            <button className="btn btn-primary" onClick={closePaymentDialog}>Done</button>
                        </div>
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default PowerMetersTab;